<script setup>
import { ref } from "vue";
import { AuthService } from "@/services/authService";
import { isSignInWithEmailLink } from "firebase/auth";
import { auth } from "@/config/firebase";
import EmailConfirmationModal from "./EmailConfirmationModal.vue";

const email = ref("");
const error = ref("");
const success = ref("");
const showEmailModal = ref(false);

const handleEmailSubmit = async () => {
  try {
    const result = await AuthService.sendLoginLink(email.value);
    if (!result.success) {
      throw new Error(result.message);
    }

    success.value = "Check your email for the login link!";
    error.value = "";
  } catch (err) {
    error.value = err.message;
    success.value = "";
  }
};

// Email confirmation callback for modal
const getEmailFromModal = () => {
  return new Promise((resolve) => {
    showEmailModal.value = true;

    const handleConfirm = (confirmedEmail) => {
      showEmailModal.value = false;
      resolve(confirmedEmail);
    };

    const handleCancel = () => {
      showEmailModal.value = false;
      resolve(null);
    };

    // Store handlers temporarily
    window.__emailModalHandlers = { handleConfirm, handleCancel };
  });
};

// Check if we're returning from an email link
const checkEmailLink = async () => {
  if (!isSignInWithEmailLink(auth, window.location.href)) return;

  try {
    const result = await AuthService.handleEmailLink(getEmailFromModal);
    if (result.success) {
      success.value = "Successfully signed in!";
      error.value = "";
    } else {
      error.value = result.message;
      success.value = "";
    }
  } catch (err) {
    error.value = err.message;
    success.value = "";
  }
};

// Handle modal events
const handleEmailConfirm = (confirmedEmail) => {
  if (window.__emailModalHandlers) {
    window.__emailModalHandlers.handleConfirm(confirmedEmail);
    delete window.__emailModalHandlers;
  }
};

const handleEmailCancel = () => {
  if (window.__emailModalHandlers) {
    window.__emailModalHandlers.handleCancel();
    delete window.__emailModalHandlers;
  }
};

// Check for email link on component mount
checkEmailLink();
</script>

<template>
  <div class="email-auth">
    <h2>Sign in with Email</h2>

    <form @submit.prevent="handleEmailSubmit" class="auth-form">
      <div class="form-group">
        <label for="email">Email Address</label>
        <input
          type="email"
          id="email"
          v-model="email"
          required
          placeholder="Enter your email"
        />
      </div>

      <button type="submit" class="submit-button">Continue</button>
      <p class="subtext">
        If you don't have an account, one will be automatically created and
        you'll be signed in.
      </p>
    </form>

    <div v-if="error" class="error-message">
      {{ error }}
    </div>

    <div v-if="success" class="success-message">
      {{ success }}
    </div>

    <!-- Email Confirmation Modal -->
    <EmailConfirmationModal
      :show="showEmailModal"
      @confirm="handleEmailConfirm"
      @cancel="handleEmailCancel"
    />
  </div>
</template>

<style scoped>
.email-auth {
  max-width: 400px;
  margin: 0 auto;
  padding: 20px;
}

.auth-form {
  display: flex;
  flex-direction: column;
  gap: 15px;
}

.form-group {
  display: flex;
  flex-direction: column;
  gap: 5px;
}

label {
  font-weight: 500;
}

input {
  padding: 8px 12px;
  border: 1px solid #ddd;
  border-radius: 4px;
  font-size: 16px;
}

.submit-button {
  background-color: #4caf50;
  color: white;
  padding: 10px 15px;
  border: none;
  border-radius: 4px;
  cursor: pointer;
  font-size: 16px;
  transition: background-color 0.3s;
}

.submit-button:hover {
  background-color: #45a049;
}

.error-message {
  color: #dc3545;
  margin-top: 10px;
  padding: 10px;
  background-color: #f8d7da;
  border-radius: 4px;
}

.success-message {
  color: #28a745;
  margin-top: 10px;
  padding: 10px;
  background-color: #d4edda;
  border-radius: 4px;
}
</style>
